// src/pages/Settings.jsx
import { useState } from 'react';
import { useAuth } from '../components/AuthWrapper';
import {
    UserCircleIcon,
    CogIcon,
    BellIcon,
    ShieldCheckIcon,
    ComputerDesktopIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [settings, setSettings] = useState({
        // Profile settings
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',

        // System settings
        useCookieAuth: import.meta.env.VITE_AUTH_USE_COOKIES === 'true',
        apiUrl: import.meta.env.VITE_API_URL || 'https://api.bidlyzer.com/graphql',

        // Notification settings
        emailNotifications: true,
        systemAlerts: true,
        crawlNotifications: true,
        assessmentNotifications: true,

        // Security settings
        sessionTimeout: '24',
        requireMFA: false,

        // Display settings
        theme: 'dark',
        timezone: 'UTC',
        dateFormat: 'MM/DD/YYYY',
        language: 'en'
    });

    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            // In a real app, this would save to the backend
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (error) {
            console.error('Failed to save settings:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const tabs = [
        { id: 'profile', name: 'Profile', icon: UserCircleIcon },
        { id: 'system', name: 'System', icon: CogIcon },
        { id: 'notifications', name: 'Notifications', icon: BellIcon },
        { id: 'security', name: 'Security', icon: ShieldCheckIcon },
        { id: 'display', name: 'Display', icon: ComputerDesktopIcon }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Settings</h1>
                {saved && (
                    <div className="bg-green-50 border border-green-200 p-2">
                        <p className="text-green-700 text-sm">Settings saved successfully!</p>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar Navigation */}
                <div className="lg:col-span-1">
                    <nav className="space-y-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center px-3 py-2 text-sm font-medium ${
 activeTab === tab.id
 ? 'bg-primary text-white'
 : 'text-gray-300 hover:bg-gray-700 hover:text-white'
 }`}
                            >
                                <tab.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Content Area */}
                <div className="lg:col-span-3">
                    <div className="bg-gray-800 p-6">
                        {activeTab === 'profile' && (
                            <ProfileSettings
                                settings={settings}
                                onChange={handleInputChange}
                                onSave={() => handleSave('profile')}
                                saving={saving}
                            />
                        )}

                        {activeTab === 'system' && (
                            <SystemSettings
                                settings={settings}
                                onChange={handleInputChange}
                                onSave={() => handleSave('system')}
                                saving={saving}
                            />
                        )}

                        {activeTab === 'notifications' && (
                            <NotificationSettings
                                settings={settings}
                                onChange={handleInputChange}
                                onSave={() => handleSave('notifications')}
                                saving={saving}
                            />
                        )}

                        {activeTab === 'security' && (
                            <SecuritySettings
                                settings={settings}
                                onChange={handleInputChange}
                                onSave={() => handleSave('security')}
                                saving={saving}
                            />
                        )}

                        {activeTab === 'display' && (
                            <DisplaySettings
                                settings={settings}
                                onChange={handleInputChange}
                                onSave={() => handleSave('display')}
                                saving={saving}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Profile Settings Component
const ProfileSettings = ({ settings, onChange, onSave, saving }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Profile Information</h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                            <input
                                type="text"
                                value={settings.firstName}
                                onChange={(e) => onChange('firstName', e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                            <input
                                type="text"
                                value={settings.lastName}
                                onChange={(e) => onChange('lastName', e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                        <input
                            type="email"
                            value={settings.email}
                            disabled
                            className="w-full bg-gray-600 border border-gray-600 text-gray-400 p-2 cursor-not-allowed"
                        />
                        <p className="text-xs text-gray-400 mt-1">Email cannot be changed from admin panel</p>
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Profile'}
                </button>
            </div>
        </div>
    );
};

// System Settings Component
const SystemSettings = ({ settings, onChange, onSave, saving }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Authentication</h3>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="text-sm font-medium text-white">Use Cookie Authentication</label>
                            <p className="text-xs text-gray-400">
                                Enable to use HttpOnly cookies instead of JWT tokens for authentication
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings.useCookieAuth}
                                onChange={(e) => onChange('useCookieAuth', e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">API URL</label>
                        <input
                            type="url"
                            value={settings.apiUrl}
                            onChange={(e) => onChange('apiUrl', e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        />
                        <p className="text-xs text-gray-400 mt-1">GraphQL endpoint for the admin console</p>
                    </div>
                </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 p-4">
                <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-700 mr-2" />
                    <div>
                        <h4 className="text-sm font-medium text-yellow-700">System Configuration</h4>
                        <p className="text-xs text-yellow-700 mt-1">
                            These settings affect how authentication works. Changes require a page refresh to take effect.
                        </p>
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save System Settings'}
                </button>
            </div>
        </div>
    );
};

// Notification Settings Component
const NotificationSettings = ({ settings, onChange, onSave, saving }) => {
    const notifications = [
        {
            key: 'emailNotifications',
            name: 'Email Notifications',
            description: 'Receive notifications via email'
        },
        {
            key: 'systemAlerts',
            name: 'System Alerts',
            description: 'Get notified about system errors and warnings'
        },
        {
            key: 'crawlNotifications',
            name: 'Crawl Notifications',
            description: 'Notifications when tender crawls complete'
        },
        {
            key: 'assessmentNotifications',
            name: 'Assessment Notifications',
            description: 'Notifications when tender assessments complete'
        }
    ];

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Notification Preferences</h3>
                <div className="space-y-4">
                    {notifications.map((notification) => (
                        <div key={notification.key} className="flex items-center justify-between">
                            <div>
                                <label className="text-sm font-medium text-white">{notification.name}</label>
                                <p className="text-xs text-gray-400">{notification.description}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings[notification.key]}
                                    onChange={(e) => onChange(notification.key, e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Notification Settings'}
                </button>
            </div>
        </div>
    );
};

// Security Settings Component
const SecuritySettings = ({ settings, onChange, onSave, saving }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Security Preferences</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Session Timeout (hours)</label>
                        <select
                            value={settings.sessionTimeout}
                            onChange={(e) => onChange('sessionTimeout', e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="1">1 hour</option>
                            <option value="4">4 hours</option>
                            <option value="8">8 hours</option>
                            <option value="24">24 hours</option>
                            <option value="72">3 days</option>
                        </select>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <label className="text-sm font-medium text-white">Require Multi-Factor Authentication</label>
                            <p className="text-xs text-gray-400">Add an extra layer of security to your account</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings.requireMFA}
                                onChange={(e) => onChange('requireMFA', e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4">
                <div className="flex">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-700 mr-2" />
                    <div>
                        <h4 className="text-sm font-medium text-blue-700">Security Information</h4>
                        <p className="text-xs text-blue-700 mt-1">
                            Your admin session is protected by Google OAuth. Additional security features may require backend implementation.
                        </p>
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Security Settings'}
                </button>
            </div>
        </div>
    );
};

// Display Settings Component
const DisplaySettings = ({ settings, onChange, onSave, saving }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-4">Display Preferences</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Theme</label>
                        <select
                            value={settings.theme}
                            onChange={(e) => onChange('theme', e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="dark">Dark</option>
                            <option value="light">Light</option>
                            <option value="auto">System</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Timezone</label>
                        <select
                            value={settings.timezone}
                            onChange={(e) => onChange('timezone', e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">Eastern Time</option>
                            <option value="America/Chicago">Central Time</option>
                            <option value="America/Denver">Mountain Time</option>
                            <option value="America/Los_Angeles">Pacific Time</option>
                            <option value="Europe/London">London</option>
                            <option value="Europe/Berlin">Berlin</option>
                            <option value="Asia/Tokyo">Tokyo</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Date Format</label>
                        <select
                            value={settings.dateFormat}
                            onChange={(e) => onChange('dateFormat', e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY (EU)</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Language</label>
                        <select
                            value={settings.language}
                            onChange={(e) => onChange('language', e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="en">English</option>
                            <option value="es">Español</option>
                            <option value="fr">Français</option>
                            <option value="de">Deutsch</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Display Settings'}
                </button>
            </div>
        </div>
    );
};

export default Settings;